import { useState, useEffect } from 'react';
import { SoundtrackService, SoundtrackStatus } from '../../../services/soundtrackService';
import { getApiUrl } from '../../../config/api';

interface UseVideoProjectProps {
  projectId: string | undefined;
  token: string | null;
}

interface VideoProjectData {
  id: string;
  title: string;
  originalVideoUrl?: string;
  captionedVideoUrl?: string;
  generatedVideoUrl?: string;
  duration?: string;
}

export const useVideoProject = ({ projectId, token }: UseVideoProjectProps) => {
  const [isVideoProject, setIsVideoProject] = useState(false);
  const [projectData, setProjectData] = useState<VideoProjectData | null>(null);
  const [soundtrackStatus, setSoundtrackStatus] = useState<SoundtrackStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const soundtrackService = new SoundtrackService();

  const loadProjectData = async () => {
    if (!projectId || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load project data
      const response = await fetch(getApiUrl(`/projects/${projectId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load project data');
      }

      const project = await response.json();
      setProjectData(project);

      // Check if this is a video project
      const hasVideo = !!(project.originalVideoUrl || project.generatedVideoUrl || project.captionedVideoUrl);
      setIsVideoProject(hasVideo);

      // If it's a video project, check soundtrack status
      if (hasVideo) {
        await loadSoundtrackStatus();
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load project';
      setError(errorMessage);
      console.error('Error loading project data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSoundtrackStatus = async () => {
    if (!projectId) return;

    try {
      const status = await soundtrackService.getSoundtrackStatus(projectId);
      setSoundtrackStatus(status);
    } catch (err) {
      console.error('Error loading soundtrack status:', err);
      // Don't set error here as soundtrack status is optional
    }
  };

  const updateSoundtrackStatus = (newStatus: SoundtrackStatus) => {
    setSoundtrackStatus(newStatus);
  };

  // Load data when projectId or token changes
  useEffect(() => {
    loadProjectData();
  }, [projectId, token]);

  return {
    isVideoProject,
    projectData,
    soundtrackStatus,
    isLoading,
    error,
    loadProjectData,
    loadSoundtrackStatus,
    updateSoundtrackStatus
  };
};
